import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  getUser, 
  deleteUser, 
  updateSubscription, 
  previewDeletionCognito,
  previewDeletionS3,
  previewDeletionCrewBookTable,
  previewDeletionShipBook
} from '../services/adminApi';

function InfoCard({ title, children }) {
  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
      {children}
    </div>
  );
}

function InfoRow({ label, value, mono = false }) {
  return (
    <div className="flex justify-between py-2 border-b border-navy-700/50 last:border-0">
      <span className="text-navy-400">{label}</span>
      <span className={`text-white ${mono ? 'font-mono text-sm' : ''}`}>{value || '-'}</span>
    </div>
  );
}

function StatBox({ label, value, color = 'teal' }) {
  return (
    <div className={`p-4 rounded-lg bg-${color}-500/10 border border-${color}-500/20`}>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className={`text-sm text-${color}-400`}>{label}</p>
    </div>
  );
}

function ScanProgress({ currentStep, completedSteps, totalSteps }) {
  const percentage = Math.round((completedSteps / totalSteps) * 100);
  
  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm text-navy-300">Scanning: {currentStep}</span>
        <span className="text-sm text-navy-400">{completedSteps}/{totalSteps}</span>
      </div>
      <div className="h-2 bg-navy-700 rounded-full overflow-hidden">
        <div 
          className="h-full bg-teal-500 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function ScanResultRow({ name, status, count, fullName }) {
  return (
    <div className="flex items-center justify-between p-2 border-b border-navy-700/30 last:border-0">
      <div className="flex items-center gap-2">
        {status === 'scanning' && (
          <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
        )}
        {status === 'done' && (
          <svg className="w-4 h-4 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )}
        {status === 'pending' && (
          <div className="w-4 h-4 rounded-full border-2 border-navy-600" />
        )}
        <span className={`${status === 'scanning' ? 'text-teal-400' : 'text-white'}`}>{name}</span>
        {fullName && <span className="text-navy-500 text-xs font-mono hidden sm:inline">({fullName})</span>}
      </div>
      {status === 'done' && count !== undefined && (
        <span className={`font-medium ${count > 0 ? 'text-coral-400' : 'text-navy-500'}`}>
          {count} record{count !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
}

function DeletePreviewModal({ userId, onConfirm, onCancel, deleting }) {
  const [scanning, setScanning] = useState(true);
  const [currentStep, setCurrentStep] = useState('Cognito');
  const [completedSteps, setCompletedSteps] = useState(0);
  const [preview, setPreview] = useState({
    userId,
    cognito: { status: 'pending', exists: false, email: null },
    s3: { status: 'pending', fileCount: 0, totalSizeMB: 0 },
    crewbook: [],
    shipbook: { status: 'pending', tables: [] },
  });

  const crewbookTables = [
    'CrewProfile', 'CrewDoc', 'PersonalDoc', 'SeaLog', 'DiverLog',
    'DiveSupLog', 'SupDiveInfo', 'ActiveNotification', 'EmploymentUpdate', 'SubscriptionStatus'
  ];

  const totalSteps = 2 + crewbookTables.length + 1; // cognito + s3 + crewbook tables + shipbook

  useEffect(() => {
    async function scan() {
      let identityId = null;

      // 1. Scan Cognito
      setCurrentStep('AWS Cognito');
      setPreview(p => ({ ...p, cognito: { ...p.cognito, status: 'scanning' } }));
      try {
        const cognitoResult = await previewDeletionCognito(userId);
        identityId = cognitoResult.identityId;
        setPreview(p => ({ 
          ...p, 
          cognito: { ...cognitoResult, status: 'done' } 
        }));
      } catch (e) {
        setPreview(p => ({ ...p, cognito: { ...p.cognito, status: 'done', error: e.message } }));
      }
      setCompletedSteps(1);

      // 2. Scan S3
      setCurrentStep('S3 Bucket');
      setPreview(p => ({ ...p, s3: { ...p.s3, status: 'scanning' } }));
      try {
        const s3Result = await previewDeletionS3(userId, identityId);
        setPreview(p => ({ ...p, s3: { ...s3Result, status: 'done' } }));
      } catch (e) {
        setPreview(p => ({ ...p, s3: { ...p.s3, status: 'done', error: e.message } }));
      }
      setCompletedSteps(2);

      // 3. Scan CrewBook tables one by one
      const crewbookResults = [];
      for (let i = 0; i < crewbookTables.length; i++) {
        const tableName = crewbookTables[i];
        setCurrentStep(tableName);
        
        // Mark as scanning
        setPreview(p => ({
          ...p,
          crewbook: [...crewbookResults, { tableName, status: 'scanning', recordCount: 0 }]
        }));

        try {
          const result = await previewDeletionCrewBookTable(userId, tableName);
          crewbookResults.push({ ...result, status: 'done' });
        } catch (e) {
          crewbookResults.push({ tableName, status: 'done', recordCount: 0, error: e.message });
        }

        setPreview(p => ({ ...p, crewbook: [...crewbookResults] }));
        setCompletedSteps(3 + i);
      }

      // 4. Scan ShipBook
      setCurrentStep('ShipBook');
      setPreview(p => ({ ...p, shipbook: { status: 'scanning', tables: [] } }));
      try {
        const shipbookResult = await previewDeletionShipBook(userId);
        setPreview(p => ({ ...p, shipbook: { status: 'done', tables: shipbookResult } }));
      } catch (e) {
        setPreview(p => ({ ...p, shipbook: { status: 'done', tables: [], error: e.message } }));
      }
      setCompletedSteps(totalSteps);

      setScanning(false);
      setCurrentStep('Complete');
    }

    scan();
  }, [userId]);

  const totalRecords = preview.crewbook.filter(t => t.status === 'done').reduce((sum, t) => sum + (t.recordCount || 0), 0) +
    (preview.shipbook.tables || []).reduce((sum, t) => sum + (t.recordCount || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/90 animate-fade-in">
      <div className="card max-w-2xl w-full max-h-[85vh] overflow-y-auto animate-slide-up">
        <div className="flex items-center gap-3 mb-6">
          <div className={`w-12 h-12 rounded-full ${scanning ? 'bg-teal-600/20' : 'bg-coral-600/20'} flex items-center justify-center`}>
            {scanning ? (
              <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-6 h-6 text-coral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">
              {scanning ? 'Scanning User Data...' : 'Confirm User Deletion'}
            </h3>
            <p className="text-navy-300 text-sm">
              {scanning ? 'Checking all tables for user records' : 'This action is permanent and cannot be undone'}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        {scanning && (
          <ScanProgress 
            currentStep={currentStep} 
            completedSteps={completedSteps} 
            totalSteps={totalSteps} 
          />
        )}

        {/* User Info */}
        {preview.cognito.status === 'done' && preview.cognito.exists && (
          <div className="mb-6 p-4 bg-navy-800 rounded-lg">
            <p className="text-sm text-navy-400 mb-1">Deleting user:</p>
            <p className="text-white font-medium">{preview.cognito.email}</p>
            <p className="text-navy-400 text-xs font-mono mt-1">{userId}</p>
          </div>
        )}

        {/* Summary (only show when done) */}
        {!scanning && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-coral-600/10 border border-coral-600/20 rounded-lg text-center">
              <p className="text-2xl font-bold text-coral-400">{totalRecords}</p>
              <p className="text-xs text-navy-400">DB Records</p>
            </div>
            <div className="p-4 bg-coral-600/10 border border-coral-600/20 rounded-lg text-center">
              <p className="text-2xl font-bold text-coral-400">{preview.s3.fileCount || 0}</p>
              <p className="text-xs text-navy-400">S3 Files</p>
            </div>
            <div className="p-4 bg-coral-600/10 border border-coral-600/20 rounded-lg text-center">
              <p className="text-2xl font-bold text-coral-400">{(preview.s3.totalSizeMB || 0).toFixed(2)}</p>
              <p className="text-xs text-navy-400">MB Storage</p>
            </div>
          </div>
        )}

        {/* Cognito */}
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${preview.cognito.status === 'scanning' ? 'bg-teal-500 animate-pulse' : preview.cognito.status === 'done' ? 'bg-coral-500' : 'bg-navy-600'}`}></span>
            AWS Cognito
          </h4>
          <div className="bg-navy-800/50 rounded-lg p-3">
            <ScanResultRow 
              name="User Account" 
              status={preview.cognito.status} 
              count={preview.cognito.exists ? 1 : 0}
            />
          </div>
        </div>

        {/* S3 */}
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${preview.s3.status === 'scanning' ? 'bg-teal-500 animate-pulse' : preview.s3.status === 'done' ? 'bg-coral-500' : 'bg-navy-600'}`}></span>
            S3 Bucket Files
          </h4>
          <div className="bg-navy-800/50 rounded-lg p-3">
            {preview.s3.status === 'scanning' ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-teal-400">Scanning files...</span>
              </div>
            ) : preview.s3.status === 'done' ? (
              <div className="flex justify-between items-center">
                <span className="text-navy-300">
                  {preview.s3.fileCount > 0 ? `${preview.s3.fileCount} files (${(preview.s3.totalSizeMB || 0).toFixed(2)} MB)` : 'No files found'}
                </span>
                {preview.s3.fileCount > 0 && <span className="text-coral-400 font-medium">Will be deleted</span>}
              </div>
            ) : (
              <span className="text-navy-500">Waiting...</span>
            )}
          </div>
        </div>

        {/* CrewBook Tables */}
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${preview.crewbook.some(t => t.status === 'scanning') ? 'bg-teal-500 animate-pulse' : preview.crewbook.length > 0 ? 'bg-coral-500' : 'bg-navy-600'}`}></span>
            CrewBook DynamoDB Tables
          </h4>
          <div className="bg-navy-800/50 rounded-lg max-h-48 overflow-y-auto">
            {crewbookTables.map((tableName) => {
              const tableData = preview.crewbook.find(t => t.tableName === tableName);
              return (
                <ScanResultRow 
                  key={tableName}
                  name={tableName} 
                  status={tableData?.status || 'pending'} 
                  count={tableData?.recordCount}
                  fullName={tableData?.fullTableName}
                />
              );
            })}
          </div>
        </div>

        {/* ShipBook Tables */}
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${preview.shipbook.status === 'scanning' ? 'bg-teal-500 animate-pulse' : preview.shipbook.status === 'done' ? 'bg-coral-500' : 'bg-navy-600'}`}></span>
            ShipBook DynamoDB Tables
          </h4>
          <div className="bg-navy-800/50 rounded-lg p-3">
            {preview.shipbook.status === 'scanning' ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-teal-400">Scanning ShipBook tables...</span>
              </div>
            ) : preview.shipbook.status === 'done' ? (
              preview.shipbook.tables && preview.shipbook.tables.length > 0 ? (
                preview.shipbook.tables.map((table) => (
                  <ScanResultRow 
                    key={table.tableName}
                    name={table.tableName} 
                    status="done" 
                    count={table.recordCount}
                  />
                ))
              ) : (
                <span className="text-navy-500">No ShipBook records found</span>
              )
            ) : (
              <span className="text-navy-500">Waiting...</span>
            )}
          </div>
        </div>

        {/* Warning (only show when done) */}
        {!scanning && (
          <div className="mb-6 p-4 bg-coral-600/10 border border-coral-600/30 rounded-lg">
            <p className="text-coral-300 text-sm">
              ⚠️ <strong>Warning:</strong> All data listed above will be permanently deleted. This includes documents, certificates, sea service records, dive logs, and any ShipBook connections. This action cannot be reversed.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="flex-1 btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={scanning || deleting}
            className="flex-1 btn-danger flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {deleting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Deleting...
              </>
            ) : scanning ? (
              'Scanning...'
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete User Permanently
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [extending, setExtending] = useState(false);

  useEffect(() => {
    async function loadUser() {
      try {
        const data = await getUser(id);
        setUser(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    loadUser();
  }, [id]);

  const handleShowDeletePreview = () => {
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteUser(id);
      navigate('/users', { replace: true });
    } catch (err) {
      alert('Failed to delete user: ' + err.message);
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleExtendSubscription = async (days) => {
    setExtending(true);
    try {
      const newExpiry = new Date();
      newExpiry.setDate(newExpiry.getDate() + days);
      await updateSubscription(id, { expiresAt: newExpiry.toISOString() });
      const data = await getUser(id);
      setUser(data);
    } catch (err) {
      alert('Failed to extend subscription: ' + err.message);
    } finally {
      setExtending(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 loading-shimmer rounded" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="h-64 loading-shimmer rounded-xl" />
            <div className="h-48 loading-shimmer rounded-xl" />
          </div>
          <div className="h-96 loading-shimmer rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-coral-600/20 flex items-center justify-center">
          <svg className="w-8 h-8 text-coral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Failed to load user</h3>
        <p className="text-navy-300 mb-4">{error}</p>
        <Link to="/users" className="btn-primary">
          Back to Users
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Delete Preview Modal */}
      {showDeleteModal && (
        <DeletePreviewModal
          userId={id}
          onConfirm={handleDelete}
          onCancel={handleCancelDelete}
          deleting={deleting}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/users"
            className="p-2 text-navy-400 hover:text-white hover:bg-navy-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white">{user.name || 'Unnamed User'}</h1>
            <p className="text-navy-300">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleShowDeletePreview}
            className="btn-danger flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete User
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile */}
          <InfoCard title="Profile Information">
            <InfoRow label="User ID" value={user.id} mono />
            <InfoRow label="Identity ID" value={user.identityId} mono />
            <InfoRow label="Email" value={user.email} />
            <InfoRow label="Phone" value={user.phone} />
            <InfoRow label="Nationality" value={user.nationality} />
            <InfoRow label="Rank" value={user.rank} />
            <InfoRow label="Created" value={user.createdAt ? new Date(user.createdAt).toLocaleString() : null} />
            <InfoRow label="Last Login" value={user.lastLogin ? new Date(user.lastLogin).toLocaleString() : null} />
          </InfoCard>

          {/* Data counts */}
          <InfoCard title="User Data">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <StatBox label="Documents" value={user.documentsCount || 0} />
              <StatBox label="Sea Logs" value={user.seaLogsCount || 0} />
              <StatBox label="Dive Logs" value={user.diveLogsCount || 0} />
              <StatBox label="Storage (MB)" value={user.storageMB?.toFixed(1) || 0} />
            </div>
          </InfoCard>

          {/* ShipBook */}
          {user.shipbookData && (
            <InfoCard title="ShipBook Connection">
              <InfoRow label="ShipCrew ID" value={user.shipbookData.shipCrewId} mono />
              <InfoRow label="Vessel" value={user.shipbookData.currentVessel} />
              <InfoRow label="Status" value={user.shipbookData.employmentStatus} />
              <InfoRow label="Contracts" value={user.shipbookData.contractsCount} />
              <InfoRow label="Employment Events" value={user.shipbookData.eventsCount} />
            </InfoCard>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <InfoCard title="Account Status">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-navy-400">Status</span>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                  user.isBlocked
                    ? 'bg-coral-500/20 text-coral-400'
                    : 'bg-teal-500/20 text-teal-400'
                }`}>
                  {user.isBlocked ? 'Blocked' : 'Active'}
                </span>
              </div>
              {user.isBlocked && user.blockedDate && (
                <div className="p-3 bg-coral-600/10 border border-coral-600/20 rounded-lg">
                  <p className="text-coral-400 text-sm">
                    Blocked since: {new Date(user.blockedDate).toLocaleDateString()}
                  </p>
                  <p className="text-coral-300 text-xs mt-1">
                    Deletion eligible: {new Date(new Date(user.blockedDate).getTime() + 60 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </InfoCard>

          {/* Subscription */}
          <InfoCard title="Subscription">
            <div className="space-y-4">
              <InfoRow label="Type" value={user.subscriptionType || 'None'} />
              <InfoRow label="Status" value={user.subscriptionActive ? 'Active' : 'Inactive'} />
              <InfoRow label="Expires" value={user.subscriptionExpiry ? new Date(user.subscriptionExpiry).toLocaleDateString() : null} />
              
              <div className="pt-4 border-t border-navy-700">
                <p className="text-sm text-navy-400 mb-3">Extend Subscription</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleExtendSubscription(30)}
                    disabled={extending}
                    className="flex-1 btn-secondary text-sm py-2"
                  >
                    +30 days
                  </button>
                  <button
                    onClick={() => handleExtendSubscription(365)}
                    disabled={extending}
                    className="flex-1 btn-secondary text-sm py-2"
                  >
                    +1 year
                  </button>
                </div>
              </div>
            </div>
          </InfoCard>

          {/* Quick stats */}
          <InfoCard title="Activity">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <svg className="w-4 h-4 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-medium">{user.documentsCount || 0} Documents</p>
                  <p className="text-xs text-navy-400">CrewDoc + PersonalDoc</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <svg className="w-4 h-4 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-medium">{user.seaLogsCount || 0} Sea Logs</p>
                  <p className="text-xs text-navy-400">Voyage records</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <svg className="w-4 h-4 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-medium">{user.diveLogsCount || 0} Dive Logs</p>
                  <p className="text-xs text-navy-400">Diving records</p>
                </div>
              </div>
            </div>
          </InfoCard>
        </div>
      </div>
    </div>
  );
}
